import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContacts, deleteContact } from '../store/slices/contactsSlice';
import ContactCard from '../components/contacts/ContactCard';
import AddContactModal from '../components/contacts/AddContactModal';
import DeleteConfirmationModal from '../components/contacts/DeleteConfirmationModal';
import toast from 'react-hot-toast';

const ContactsPage = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector((state) => state.contacts);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchContacts());
  }, [dispatch]);

  const handleDelete = (contact) => {
    setContactToDelete(contact);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (contactToDelete) {
      dispatch(deleteContact(contactToDelete.id)).then((result) => {
        if (result.meta.requestStatus === 'fulfilled') {
          toast.success('Contact deleted successfully!');
        } else {
          toast.error('Failed to delete contact');
        }
      });
    }
    setDeleteModalOpen(false);
    setContactToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setContactToDelete(null);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingContact(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingContact(null);
  };

  const handleContactSuccess = () => {
    // Optionally refresh contacts or handle success
    console.log('Contact operation completed successfully');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-between py-3 border border-[#E5E6E8] px-4 md:px-12 bg-white">
        <h1 className="text-base md:text-2xl font-semibold">Contacts</h1>
        <button
          onClick={handleAddNew}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
        >
          + New Contact
        </button>
      </div>

      {/* Add/Edit Contact Modal */}
      <AddContactModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        contact={editingContact}
        onSuccess={handleContactSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        contactName={contactToDelete?.name || ''}
      />

      {/* Contacts Grid */}
      <div className="px-4 md:px-12 pt-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {list.length > 0 ? (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {/* The address book is owner-scoped end-to-end (BE filters by
                    added_by), so deleting one's own contacts needs no club
                    permission — ContactCard defaults to canDelete. */}
                {list.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No contacts found.</p>
                <p className="text-gray-400 text-sm mt-2">Add your first contact to get started.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContactsPage; 